// src/components/FeatureSubsection.jsx
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FeatureRequirementCard from './FeatureRequirementCard';
import { useFeatureExpand } from '../context/FeatureExpandContext';

const FeatureSubsection = ({
  features = [],
  responses = {},
  onResponseChange,
  comments = {},
  onCommentAdd,
  onReactionAdd,
  reactions = [],
  teamResponses = [],
  currentUser,
  organization,
  showAllSections = false,
  featureDependencies = {},
  dependentFeatures = {},
  onNavigateToFeature,
  fetchDependencies,
  updateDependencyTracking,
  fetchConflicts,
  lastUpdate,
  activeOperations = new Set(), // Add this line with a default value
}) => {
  const { isExpanded, expandAll, collapseAll, toggleFeature, allExpanded } = useFeatureExpand();

  if (!features || features.length === 0) {
    return <div>No features available for this section.</div>;
  }

  // Deduplicate features based on `feature.reference`
  const uniqueFeatures = Array.from(
    new Map(features.map((feature) => [feature.reference, feature])).values()
  );

  //console.log('[DEBUG] Features array:', features);

  const findDuplicates = (array) =>
    array.filter((item, index) => array.indexOf(item) !== index);

  const duplicateReferences = findDuplicates(features.map((f) => f.reference));
  if (duplicateReferences.length > 0) {
    console.warn('[DEBUG] Duplicate feature references found:', duplicateReferences);
  }
  //console.log('[DEBUG] Unique features:', uniqueFeatures);

  return (
    <div>
      <div className="flex justify-end items-center gap-3 mb-6">
        <button
          onClick={expandAll}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md
            ${
              allExpanded
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          disabled={allExpanded}
        >
          <ChevronDown className="w-4 h-4" />
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md
            ${
              !allExpanded && features.every((f) => !isExpanded(f.reference))
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          disabled={!allExpanded && features.every((f) => !isExpanded(f.reference))}
        >
          <ChevronUp className="w-4 h-4" />
          Collapse All
        </button>
      </div>

      <div className="space-y-6">
        {uniqueFeatures.map((feature, index) => (
          <FeatureRequirementCard
            key={`${feature.reference}-${index}`}
            feature={feature}
            features={features}
            userResponse={responses[feature.reference]}
            onResponseChange={onResponseChange}
            comments={comments[feature.reference] || []}
            onCommentAdd={onCommentAdd}
            onReactionAdd={onReactionAdd}
            reactions={reactions}
            teamResponses={teamResponses}
            currentUser={currentUser}
            organization={organization}
            isExpanded={isExpanded(feature.reference)}
            onToggleExpand={() => toggleFeature(feature.reference)}
            showAllSections={showAllSections}
            featureDependencies={featureDependencies}
            dependentFeatures={dependentFeatures}
            onNavigateToFeature={onNavigateToFeature}
            fetchDependencies={fetchDependencies}
            fetchConflicts={fetchConflicts}
            updateDependencyTracking={updateDependencyTracking}
            lastUpdate={lastUpdate}
            activeOperations={activeOperations} // Add this line
          />
        ))}
      </div>
    </div>
  );
};

export default FeatureSubsection;